import React from 'react';
import './EntryOverlay.css';
import { FloatingLogos } from './FloatingLogos';
import { EntrySection } from './EntrySection';

interface WorkHistoryEntryProps {
  onReturnToMap: () => void;
  entryPosition?: { x: number; y: number };
  onNext?: () => void;
  nextLabel?: string;
}

export const WorkHistoryEntry: React.FC<WorkHistoryEntryProps> = ({
  onReturnToMap,
  onNext,
  nextLabel,
}: WorkHistoryEntryProps) => {
  return (
    <div className="entry-overlay">
      <FloatingLogos />
      <div className="entry-card accent-work">
        <div className="entry-header">
          <h2 className="entry-title">Professional Experience</h2>
        </div>

        <div className="entry-body">
          <div className="entry-description">
            <p>
              Full-stack engineer with 7+ years at{' '}
              <a
                href="https://www.fidelity.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--accent)', textDecoration: 'underline' }}
              >
                Fidelity Investments
              </a>, growing from intern to principal by going wide across
              frontend, backend, DevOps, and product strategy. I ship software
              that scales and works for people — millions of users, hundreds of
              engineers, and the systems they depend on.
            </p>
          </div>

          <EntrySection title="Frontend Development & Architecture">
            <div className="entry-description">
              <p>
                I've built across React, Vue, and Angular in enterprise
                environments, designing monorepo architecture (
                <a
                  href="https://nx.dev/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  NX
                </a>
                ), shared JavaScrpt/TypeScript
                libraries, and reusable component system that enabled 100+
                engineers across 30+ applications to ship faster and
                consistently to our millions of users. Owned the full 
                pipeline from code to production— ensuring quality, 
                security, and reliability so teams couldfocus on 
                delivering features to real customers.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="Accessibility & User Experience">
            <div className="entry-description">
              <p>
                Accessibility isn't a checkbox for me. I've built{' '}
                <a
                  href="https://www.w3.org/WAI/standards-guidelines/wcag/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  WCAG-compliant
                </a>{' '}
                interfaces for healthcare applications where the users range from
                twenty-somethings to retirees managing their benefits for the first
                time. That means semantic HTML, proper ARIA patterns, keyboard
                navigation, screen reader testing, and responsive layouts that
                actually work on the devices people use - not just the ones we test
                on. When you're building for over a million users in healthcare,
                getting this wrong isn't just bad UX, it's excluding people from
                managing their own health.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="API Architecture & Backend Development">
            <div className="entry-description">
              <p>
                Built high-throughput, HIPAA-compliant backend systems: an{' '}
                <a
                  href="https://www.apollographql.com/docs/federation/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  Apollo Federation
                </a>{' '}
                GraphQL layer in Node.js/TypeScript (15 EKS services,
                ~1M LOC) serving 3M+ requests/day. Actively developing micro-services in Java/Spring Boot
                to support mission critical migrational efforts. Implemented role-based
                  scopes and fine-grained authorization, and designed
                  performance-focused Node.js/{' '}
                  <a
                    href="https://www.fastify.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                  >
                    Fastify
                  </a>{' '}
                  services for secure storage of Healthcare documents. Established cross-team schema governance and CI/CD
                deployment practices to ensure compliance, resilience, and safe
                rollouts.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="DevOps & Infrastructure">
            <div className="entry-description">
              <p>
                Built end-to-end CI/CD pipelines and infrastructure for a
                healthcare monorepo: Kubernetes with Helm charts, serverless
                functions, CDN and object storage, infrastructure-as-code, and
                artifact registries. Designed CI/CD pipelines using{' '}
                <a
                  href="https://www.jenkins.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  Jenkins
                </a>{' '}
                and{' '}
                <a
                  href="https://docs.github.com/en/actions"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)', textDecoration: 'underline' }}
                >
                  GitHub Actions
                </a>{' '}
                with quality gates and automated deployments that incorporate
                security scans, static analysis, unit and integration tests,
                bundling, and artifact publishing. Mentored hundreds of
                engineers to debug pipelines and own their delivery systems;
                proud to have shifted the culture to treat infrastructure as a
                shared responsibility.
              </p>
            </div>
          </EntrySection>

          <EntrySection title="Feature Delivery Strategy & Product">
            <div className="entry-description">
              <p>
                Integrated an enterprise feature-toggle vendor SDK and stood up a
                Spring Boot toggle service backed by GraphQL/REST contracts for
                client and static apps. Defined the client-service toggle contract
                and common consumption architecture so teams could adopt flags
                without bespoke integrations. Rolled it out through alpha and
                beta teams, captured feedback, documented standards, and ran
                training sessions to enable safer, more frequent production-
                tested releases.
              </p>
            </div>
          </EntrySection>
        </div>

        <div className="entry-footer">
          <button className="entry-button" onClick={onReturnToMap}>
            &larr; Return to Map
          </button>
          {onNext && (
            <button className="entry-button" onClick={onNext}>
              Next: {nextLabel} &rarr;
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
